import React, { useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, Radio, Clock } from 'lucide-react';
import Newsletter from '../components/Newsletter';
import ArticleReferences from '../components/ArticleReferences';
import ArticleStats from '../components/ArticleStats';
import ShareButtons from '../components/ShareButtons';
import RelatedArticles from '../components/RelatedArticles';
import AuthorBadge from '../components/AuthorBadge';
import { news } from '../mock';

const NewsArticle = () => {
  const { id } = useParams();
  const article = news.find((n) => n.id === id);
  const related = news.filter((n) => n.id !== id).slice(0, 3);

  useEffect(() => {
    if (article) {
      document.title = `${article.title} | DCTV`;
      return () => { document.title = 'Diaspora Community Television | Your story. Your screen. Your community.'; };
    }
  }, [article]);

  if (!article) {
    return (
      <div className="pt-[160px] pb-24 px-6 lg:px-10 text-center">
        <h1 className="font-serif-dctv text-4xl font-bold text-white">Story not found</h1>
        <Link to="/news" className="dctv-btn-primary inline-flex items-center gap-2 px-6 py-3 rounded-full text-sm font-semibold mt-8">
          <ArrowLeft className="w-4 h-4" /> Back to news
        </Link>
      </div>
    );
  }

  return (
    <div>
      <article className="pt-[120px] pb-10 px-6 lg:px-10">
        <div className="max-w-[820px] mx-auto fade-up">
          <Link to="/news" className="inline-flex items-center gap-2 text-[#9aa3b2] hover:text-[#e8743b] text-sm font-medium transition-colors">
            <ArrowLeft className="w-4 h-4" /> All news
          </Link>
          <div className="flex flex-wrap items-center gap-3 mt-7">
            <span className="dctv-eyebrow text-[#e8743b]">{article.theme || article.category}</span>
            <span className="text-[#6b7384] text-xs">{article.date}</span>
            {article.readTime && (
              <span className="inline-flex items-center gap-1 text-[#6b7384] text-xs"><Clock className="w-3.5 h-3.5" /> {article.readTime}</span>
            )}
          </div>
          <h1 className="font-serif-dctv text-4xl md:text-5xl font-bold text-white mt-4 leading-[1.12]">{article.title}</h1>
          <p className="text-[#bcc3cf] text-lg leading-relaxed mt-5">{article.excerpt}</p>
          <div className="mt-7 pb-7 border-b border-white/8">
            <AuthorBadge author={article.author} size="xl" link />
            <p className="text-[#6b7384] text-xs mt-2">Tap the author to see all their stories.</p>
            <div className="mt-5"><ShareButtons title={article.title} /></div>
          </div>
        </div>

        <div className="max-w-[980px] mx-auto mt-10">
          {article.image ? (
            <div className="rounded-2xl overflow-hidden border border-white/8">
              <img src={article.image} alt={article.title} className="w-full h-[300px] md:h-[460px] object-cover" />
            </div>
          ) : (
            <div className="rounded-2xl border border-white/8 h-[240px] flex items-center justify-center bg-gradient-to-br from-[#e8743b]/15 to-[#0c1119]">
              <Radio className="w-14 h-14 text-[#e8743b]" />
            </div>
          )}
        </div>

        <div className="max-w-[820px] mx-auto mt-10 space-y-6">
          {article.body.map((para, i) => (
            <p key={para.slice(0, 40)} className={`text-[#cdd2dc] leading-[1.85] ${i === 0 ? 'text-[19px]' : 'text-[17px]'}`}>{para}</p>
          ))}
          <ArticleStats stats={article.stats} />
          <ArticleReferences references={article.references} />
          <div className="pt-8 border-t border-white/8"><ShareButtons title={article.title} /></div>
        </div>
      </article>

      <RelatedArticles articles={related} />
      <Newsletter />
    </div>
  );
};

export default NewsArticle;
